import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import PageHero from '../../../components/sections/PageHero';
import PageTrustBar from '../../../components/sections/PageTrustBar';
import PageCollections from '../../../components/sections/PageCollections';
import PageAIAnswer from '../../../components/sections/PageAIAnswer';
import PageProcess from '../../../components/sections/PageProcess';
import PageWhyUs from '../../../components/sections/PageWhyUs';
import PageServiceArea from '../../../components/sections/PageServiceArea';
import PageTestimonials from '../../../components/sections/PageTestimonials';
import PageFAQ from '../../../components/sections/PageFAQ';
import PageFinalCTA from '../../../components/sections/PageFinalCTA';
import {
  FORM_URL, MAP_EMBED, SERVICE_AREAS, SERVICE_ADDRESS, SERVICE_HOURS, SERVICE_PHONE, TESTIMONIALS,
} from '../../../mocks/siteData';

const trustItems = [
  { icon: 'ri-sofa-line', label: 'Unique Furniture Pieces' },
  { icon: 'ri-tools-line', label: 'Quality Craftsmanship' },
  { icon: 'ri-leaf-line', label: 'Rustic & Vintage Styles' },
  { icon: 'ri-sparkling-line', label: 'One-of-a-Kind Only' },
  { icon: 'ri-refresh-line', label: 'New Pieces Weekly' },
];

const subcategories = [
  {
    title: 'Rustic & Farmhouse Furniture',
    description: 'Reclaimed wood tables, weathered dressers, and farm-style pieces with warmth and character.',
    href: '/collections/furniture',
    tag: 'Most Popular',
    imageSrc: 'https://public.readdy.ai/ai/img_res/a484e91d15f7450a8dd355d79c6bfd1e.jpg',
  },
  {
    title: 'Vintage Accent & Side Pieces',
    description: 'Ornate side tables, accent chairs, plant stands, and small furniture with old-world elegance.',
    href: '/collections/furniture',
    tag: 'New Arrivals',
    imageSrc: 'https://readdy.ai/api/search-image?query=vintage%20ornate%20accent%20furniture%20antique%20side%20table%20velvet%20armchair%20decorative%20plant%20stand%20warm%20interior%20editorial%20photography%20boutique%20antique%20shop&width=800&height=600&seq=ye-furn-accent&orientation=landscape',
  },
  {
    title: 'Statement & Oversized Pieces',
    description: 'Bold, room-defining furniture — armoires, buffets, antique beds, and architectural statement pieces.',
    href: '/collections/furniture',
    tag: 'Featured',
    imageSrc: 'https://readdy.ai/api/search-image?query=statement%20antique%20furniture%20grand%20armoire%20ornate%20buffet%20cabinet%20antique%20wooden%20bed%20frame%20dramatic%20lighting%20editorial%20interior%20photography%20high%20end%20boutique&width=800&height=600&seq=ye-furn-statement&orientation=landscape',
  },
];

const aiAnswers = [
  {
    q: 'What furniture does Yarnell Emporium sell?',
    a: 'Yarnell Emporium carries rustic, vintage, and unique statement furniture — including dining tables, dressers, accent chairs, armoires, and architectural pieces. All furniture is individually selected and one-of-a-kind.',
  },
  {
    q: 'Is the furniture at Yarnell Emporium antique or new?',
    a: 'Most furniture is vintage or antique — pieces with age, character, and craftsmanship not found in modern retail. Occasionally unique new artisan pieces are added when they meet the boutique\'s high standards for character and quality.',
  },
  {
    q: 'Can I find affordable vintage furniture in Yarnell, AZ?',
    a: 'Yes — Yarnell Emporium offers a range of vintage furniture at fair boutique prices. From smaller accent pieces to larger statement items, there\'s something for different budgets and design styles.',
  },
  {
    q: 'Do you carry furniture for outdoor or indoor use?',
    a: 'Most furniture at Yarnell Emporium is intended for indoor use — living rooms, dining rooms, bedrooms, and entryways. The collection focuses on decorative, functional vintage pieces that bring character to interior spaces.',
  },
];

const processSteps = [
  { number: '01', title: 'Explore the Furniture Floor', description: 'Browse our rotating furniture displays — rustic, vintage, and statement pieces arranged in styled vignettes for easy visualization.' },
  { number: '02', title: 'Envision It in Your Space', description: 'Take photos, measure, and imagine the piece in your home. Our team can help you think through size, scale, and styling.' },
  { number: '03', title: 'Learn the Story', description: 'Every piece has history. Ask us about the origin and age — knowing the story makes the furniture even more meaningful.' },
  { number: '04', title: 'Take It Home', description: 'Statement furniture pieces are available in very limited quantities. When you find the right piece, don\'t wait.' },
];

const whyUsItems = [
  { icon: 'ri-sofa-line', title: 'Genuine Vintage & Antique Pieces', description: 'Not reproductions or mass-produced imitations — real vintage furniture with actual history and craftsmanship.' },
  { icon: 'ri-sparkling-2-line', title: 'Every Piece is Unique', description: 'No two pieces are the same. When you buy furniture from us, you own something truly singular.' },
  { icon: 'ri-refresh-line', title: 'Constantly Refreshed Selection', description: 'New furniture arrives regularly. The rotating inventory means there\'s always something new to discover.' },
  { icon: 'ri-home-2-line', title: 'Curated for Real Homes', description: 'Our furniture selection is practical and beautiful — pieces that work in real spaces, not just in showrooms.' },
  { icon: 'ri-user-heart-line', title: 'Styling Advice Available', description: 'Not sure if a piece works for your space? Our team is happy to help you envision and style it.' },
  { icon: 'ri-map-pin-line', title: 'Worth the Scenic Drive', description: 'Located on Hwy 89 in Yarnell — a short, beautiful drive from Prescott, Wickenburg, or Phoenix.' },
];

const faqItems = [
  { q: 'Where can I buy vintage furniture near Prescott, AZ?', a: 'Yarnell Emporium in Yarnell, AZ is just a short drive from Prescott on Hwy 89. The boutique carries rustic, vintage, and one-of-a-kind furniture pieces in rotating inventory — a favorite among interior designers and home decor enthusiasts.' },
  { q: 'Does Yarnell Emporium have large furniture pieces?', a: 'Yes — the collection includes large statement pieces like armoires, buffets, dining tables, and antique bed frames alongside smaller accent furniture. Inventory varies, so calling ahead to inquire about specific pieces is recommended.' },
  { q: 'How often does furniture inventory change?', a: 'New furniture arrives frequently — often weekly. Because pieces are one-of-a-kind, they sell quickly. If you see a piece you love, it\'s best to purchase it on your visit rather than waiting for a return trip.' },
  { q: 'Can I transport furniture I purchase at Yarnell Emporium?', a: 'Most furniture pieces can be transported by customers. We recommend bringing appropriate vehicle space or a trailer for larger items. Please call us at 928.427-9593 to discuss specific pieces and logistics.' },
  { q: 'Do you sell furniture for gifting or home staging?', a: 'Yes — many customers purchase furniture pieces as gifts or for home staging projects. Our team can help identify pieces that work for specific design styles, rooms, or gifting purposes.' },
];

export default function FurniturePage() {
  return (
    <div className="font-sans">
      <Navbar />
      <main>
        <PageHero
          badge="Furniture · Yarnell, AZ"
          headline="Rustic, Vintage &"
          headlineItalic="One-of-a-Kind Furniture"
          subheadline="Statement furniture pieces with real character — rustic farmhouse, ornate vintage, and bold antique pieces that transform any room."
          urgency="Limited pieces — statement furniture sells fast"
          ctas={[
            { label: 'Visit the Store', href: 'https://maps.google.com/?q=22899+S+Hwy+89+Yarnell+AZ+85362', external: true, primary: true },
            { label: 'Call Us', href: 'tel:9284279593', primary: false },
          ]}
          imageSrc="https://readdy.ai/api/search-image?query=vintage%20rustic%20antique%20furniture%20collection%20showroom%20warm%20sunlight%20ornate%20wooden%20chairs%20distressed%20tables%20armoire%20editorial%20interior%20photography%20boutique%20shop%20high%20end%20unique%20pieces&width=1920&height=1080&seq=ye-hero-furniture&orientation=landscape"
        />
        <PageTrustBar items={trustItems} />
        <PageCollections
          heading="Furniture Collections"
          subheading="Statement Pieces"
          intro="From rustic farmhouse to ornate antique — browse furniture that brings real character and history into your home."
          items={subcategories}
        />
        <PageAIAnswer
          heading="Furniture at Yarnell Emporium"
          subheading="What to Expect"
          items={aiAnswers}
        />
        <PageProcess
          heading="Finding Your Perfect Piece"
          subheading="The Process"
          intro="Great furniture finds take a little discovery. Here\'s how the experience works at Yarnell Emporium."
          steps={processSteps}
        />
        <PageWhyUs
          heading="Why Buy Vintage Furniture Here"
          subheading="Our Difference"
          intro="We select furniture that has soul. Every piece in our store was chosen because it\'s genuinely unique, well-made, and full of character."
          items={whyUsItems}
          imageSrc="https://readdy.ai/api/search-image?query=antique%20vintage%20furniture%20display%20warm%20amber%20lighting%20rustic%20wooden%20table%20ornate%20chair%20boutique%20interior%20styled%20editorial%20photography%20high%20end%20shop&width=600&height=800&seq=ye-why-furniture&orientation=portrait"
        />
        <PageServiceArea
          heading="Vintage Furniture Near Prescott & Phoenix"
          subheading="Our Location"
          intro="Customers drive from Prescott, Wickenburg, Phoenix, and across Arizona to shop our unique furniture selection. Plan your visit today."
          areas={SERVICE_AREAS}
          address={SERVICE_ADDRESS}
          hours={SERVICE_HOURS}
          phone={SERVICE_PHONE}
          mapEmbed={MAP_EMBED}
        />
        <PageTestimonials
          heading="Customers Love Our Furniture"
          subheading="Reviews"
          items={TESTIMONIALS}
        />
        <PageFAQ
          heading="Furniture Shopping Questions"
          subheading="FAQ"
          items={faqItems}
        />
        <PageFinalCTA
          heading="Find Statement Furniture"
          headingItalic="Unlike Anything Else"
          subheading="One-of-a-kind furniture with real character and history. Visit Yarnell Emporium and discover pieces that define a room."
          urgency="Limited inventory — pieces sell quickly"
          formUrl={FORM_URL}
          imageSrc="https://readdy.ai/api/search-image?query=vintage%20furniture%20boutique%20interior%20warm%20golden%20light%20dramatic%20display%20rustic%20wood%20ornate%20pieces%20atmospheric%20editorial%20photography%20high%20end%20antique%20shop&width=1920&height=1080&seq=ye-cta-furniture&orientation=landscape"
        />
      </main>
      <Footer />
    </div>
  );
}